import {
  setSelectedProfile,
} from "@app-redux/slices/profileListSlice";
import { useAppDispatch } from "@app-redux/store";
import { useCategories, useUserData, useProfileListWithFilters } from "@/hooks/queries";
import { useIsMobile } from "@/hooks/useResponsive";
import type { ProfileRes } from "@/types/profile";
import { CountryFlag } from "@components/CountryFlag";
import { RelationshipLevelSelect } from "@components/RelationshipLevelSelect";
import { SortMenu } from "@components/SortMenu";
import { ROUTER_PATHS } from "@constants/index";
import Avatar from "@mui/material/Avatar";
import {
  getCategoryItem,
  getSortedCategories,
  stringAvatar,
} from "@utils/index";
import { Suspense, lazy, memo, forwardRef } from "react";
import { TableVirtuoso, Virtuoso } from "react-virtuoso";
import { Link } from "react-router-dom";
import styles from "./styles.module.css";

const NothingFound = lazy(() => import("@components/NothingFound"));

function fixedHeaderContent() {
  return (
    <tr>
      <th className={styles.rowFilter}>
        Person <SortMenu />
      </th>
      <th>Relationship Level</th>
      <th>Role</th>
      <th>Market</th>
      <th>Country</th>
      <th>Rep</th>
    </tr>
  );
}

const TableComponent = forwardRef<HTMLTableElement, React.ComponentPropsWithRef<"table">>(
  ({ style, ...props }, ref) => (
    <table
      ref={ref}
      {...props}
      style={style}
      aria-label="Profile table"
      className={styles.table}
    >
      <colgroup span={6}>
        <col width="22%" />
        <col width="15%" />
        <col width="15%" />
        <col width="15%" />
        <col width="15%" />
        <col width="18%" />
      </colgroup>
      {props.children}
    </table>
  )
);

const TableBodyComponent = forwardRef<HTMLTableSectionElement, React.ComponentPropsWithRef<"tbody">>(
  (props, ref) => <tbody {...props} ref={ref} />
);

const ProfileTableRowContent = memo(function ProfileTableRowContent({
  item,
  index,
}: {
  item: ProfileRes;
  index: number;
}) {
  const dispatch = useAppDispatch();
  const { data: categoryList, map, isCategoryMap } = useCategories();
  const {
    id = "",
    name = "",
    image_url = "",
    roles_ids = [],
    reps_ids = [],
    markets_ids = [],
    locations_ids = [],
    relationship_level,
  } = item || {};

  const sortedRoles = getSortedCategories(roles_ids, map, !!isCategoryMap);
  const sortedReps = getSortedCategories(reps_ids, map, !!isCategoryMap);
  const sortedMarkets = getSortedCategories(markets_ids, map, !!isCategoryMap);
  const sortedLocations = getSortedCategories(
    locations_ids,
    map,
    !!isCategoryMap
  );

  const getCategoryNames = (arr: string[]) =>
    arr.map((id) => getCategoryItem(id, categoryList, map)?.name).join(", ");

  const getCountryNames = (arr: string[]) => {
    return arr.map((id) => {
      const item = getCategoryItem(id, categoryList, map);
      return item ? item.name : "";
    });
  };

  const onClickHandler = () => {
    dispatch(
      setSelectedProfile({
        id: id,
        index,
      })
    );
  };

  return (
    <>
      <td aria-label={`Link to profile: ${name}`}>
        <div className={styles.profileName}>
          <Avatar alt={name} src={image_url} {...stringAvatar(name)} />
          <Link to={`/${ROUTER_PATHS.profile}/${id}`}>
            {<span onClick={onClickHandler}>{name}</span>}
          </Link>
        </div>
      </td>
      <td>
        <RelationshipLevelSelect
          profileId={id}
          currentLevel={relationship_level}
        />
      </td>
      <td>{getCategoryNames(sortedRoles)}</td>
      <td>{getCategoryNames(sortedMarkets)}</td>
      <td>
        {getCountryNames(sortedLocations).map((item) => (
          <div key={item} className={styles.countryBlock}>
            <CountryFlag country={item}/>
            <p>{item}</p>
          </div>
        ))}
      </td>
      <td>{getCategoryNames(sortedReps)}</td>
    </>
  );
});

const ProfileCardContent = memo(function ProfileCardContent({
  item,
  index,
}: {
  item: ProfileRes;
  index: number;
}) {
  const dispatch = useAppDispatch();
  const { data: categoryList, map, isCategoryMap } = useCategories();
  const {
    id = "",
    name = "",
    image_url = "",
    roles_ids = [],
    reps_ids = [],
    markets_ids = [],
    relationship_level,
  } = item || {};

  const sortedRoles = getSortedCategories(roles_ids, map, !!isCategoryMap);
  const sortedReps = getSortedCategories(reps_ids, map, !!isCategoryMap);
  const sortedMarkets = getSortedCategories(markets_ids, map, !!isCategoryMap);

  const getCategoryNames = (arr: string[]) =>
    arr.map((id) => getCategoryItem(id, categoryList, map)?.name).join(", ");

  return (
    <div className={styles.card}>
      <Avatar
        alt={name}
        src={image_url}
        {...stringAvatar(name)}
        className={styles.cardAvatar}
      />
      <div className={styles.cardBody}>
        <Link
          to={`/${ROUTER_PATHS.profile}/${id}`}
          onClick={() => dispatch(setSelectedProfile({ id, index }))}
          className={styles.cardName}
        >
          {name}
        </Link>
        <div className={styles.cardMeta}>
          {getCategoryNames(sortedRoles) && (
            <span><span className={styles.cardMetaLabel}>Role:</span> {getCategoryNames(sortedRoles)}</span>
          )}
          {getCategoryNames(sortedMarkets) && (
            <span><span className={styles.cardMetaLabel}>Market:</span> {getCategoryNames(sortedMarkets)}</span>
          )}
          {getCategoryNames(sortedReps) && (
            <span><span className={styles.cardMetaLabel}>Rep:</span> {getCategoryNames(sortedReps)}</span>
          )}
        </div>
        <div className={styles.cardLevel}>
          <RelationshipLevelSelect
            profileId={id}
            currentLevel={relationship_level}
          />
        </div>
      </div>
    </div>
  );
});

export const ProfileTable = () => {
  const isMobile = useIsMobile();
  const { data: userData } = useUserData();
  const isUserData = !!userData;
  const { data: profileListData, isLoading, fetchNextPage, hasNextPage } = useProfileListWithFilters();
  const { isLoading: isCategoryLoading } = useCategories();

  const profileList = profileListData?.pages.flatMap((p) => p.data) ?? [];
  const count = profileListData?.pages[0]?.count ?? 0;

  if (isMobile) {
    return (
      <>
        <Virtuoso
          style={{ height: "100%" }}
          data={profileList}
          endReached={() => hasNextPage && fetchNextPage()}
          overscan={200}
          computeItemKey={(_: number, item: ProfileRes) => item.id}
          itemContent={(index: number, item: ProfileRes) => (
            <ProfileCardContent item={item} index={index} />
          )}
        />
        {isUserData && !isLoading && !isCategoryLoading && !count && (
          <Suspense fallback="">
            <NothingFound />
          </Suspense>
        )}
      </>
    );
  }

  return (
    <>
      <TableVirtuoso<ProfileRes>
        style={{ height: "100%" }}
        data={profileList}
        endReached={() => hasNextPage && fetchNextPage()}
        overscan={200}
        computeItemKey={(_: number, item: ProfileRes) => item.id}
        fixedHeaderContent={fixedHeaderContent}
        itemContent={(index: number, item: ProfileRes) => (
          <ProfileTableRowContent item={item} index={index} />
        )}
        components={{
          Table: TableComponent,
          TableBody: TableBodyComponent,
        }}
      />
      {isUserData && !isLoading && !isCategoryLoading && !count && (
        <Suspense fallback="">
          <NothingFound />
        </Suspense>
      )}
    </>
  );
};
